import React from 'react';
import { Box, Grid, GridItem, Icon, Notification, Typography } from '@drone-js/core';
import { connect } from 'react-redux';
import PaxCounterContainer from 'component/page/detail/PackageOptions/ExpandedOffer/OfferCardPaxPanel/PaxCounter/PaxCounterContainer';
import { mapDispatchToProps, mapStateToProps, PaxPanelProps } from 'component/page/detail/PackageOptions/ExpandedOffer/OfferCardPaxPanel/OfferCardPaxPanel.selectors';
import { withErrorBoundary } from 'component/common/Hoc/ErrorBoundary';
import Experiment from 'component/common/Experiment/Experiment';
import { ExperimentId } from 'core/type/ExperimentId';
import { StringFormatter } from '@agoda/react-handyman';
import { FeatureName } from 'core/constant/FeatureName';
import Feature from 'component/common/Experiment/Feature';
import { InView } from 'react-intersection-observer';
import { trackElementEvent } from 'core/messaging/analytics/analytics';
import { ActivitiesAlertMessageSeenEventActivitiesFieldsV1 } from '@agoda/analytics-messages-definition-activities/events.split';
import { ActivitiesAlertMessageSeenEventActivitiesManifestV1 } from '@agoda/analytics-messages-definition-activities/manifest/events';

const ALMOST_SOLD_OUT_THRESHOLD = 10;

const PaxCounterSection: React.FC<PaxPanelProps> = (props) => {
    return (
        <div data-testid="booking-options-pax-availability-panel">
            <Grid spacing="4">
                <PaxCounterContainer
                    paxRequirements={props.selectedBookingRequirement.paxRequirements}
                    availableQuantity={props.availableQuantity}
                    paxState={props.paxState}
                    setPaxState={props.setPassengers}
                    inProgress={props.inProgress}
                    selectedDate={props.selectedDate}
                    isAllOfferSpecSelected={props.isAllOfferSpecSelected}
                    isTotalReachedMaxPax={props.isTotalReachedMaxPax}
                    setOfferNotification={props.setOfferNotification}
                    activityId={props.activityId}
                />
            </Grid>
        </div>
    );
};
PaxCounterSection.displayName = 'PaxCounterSection';

const BookingRequirementSection: React.FC<PaxPanelProps> = (props) => {
    const title = props.isTicketTitle ? props.cms[223437] : props.cms[130771];
    const availableQuantityCms = props.isUpdateAvailabilitySlotAlert ? props.cms[409793] : props.cms[344618];
    const availableQuantityMessage = StringFormatter.format(availableQuantityCms, props.availableQuantity);
    const onSeen = (inView: boolean, value: string) => {
        if (!inView) return;
        trackElementEvent<ActivitiesAlertMessageSeenEventActivitiesFieldsV1>(ActivitiesAlertMessageSeenEventActivitiesManifestV1.ids, {
            action_element_value: value,
        });
    };
    const alertMessage = props.isUpdateAvailabilitySlotAlert ? (
        <>
            {props.isAllOfferSpecSelected && props.errorMessage ? (
                <InView onChange={(inView) => onSeen(inView, props.errorMessage)} triggerOnce={true} threshold={1}>
                    <Box display="flex" data-testid="alert-message" data-element-value={props.errorMessage} mt="8" me="4">
                        <Icon id="fill.symbol.circle-info" color="feedback.notice" size="14" />
                        <Typography variant="caption-default" textColor="feedback.notice" ms="4">
                            {props.errorMessage}
                        </Typography>
                    </Box>
                </InView>
            ) : (
                <Experiment.WhenB id={ExperimentId.ACT_4598}>
                    {props.availableQuantity <= ALMOST_SOLD_OUT_THRESHOLD && (
                        <InView onChange={(inView) => onSeen(inView, availableQuantityMessage)} triggerOnce={true} threshold={1}>
                            <Box display="flex" data-testid="alert-message" data-element-value={availableQuantityMessage} mt="8" me="4">
                                <Icon id="fill.symbol.lightning1" color="feedback.notice" size="14" />
                                <Typography variant="caption-default" textColor="feedback.notice" ms="4">
                                    {availableQuantityMessage}
                                </Typography>
                            </Box>
                        </InView>
                    )}
                </Experiment.WhenB>
            )}
        </>
    ) : (
        <>
            <Experiment.WhenB id={ExperimentId.ACT_4598}>
                {props.availableQuantity <= ALMOST_SOLD_OUT_THRESHOLD && (
                    <InView onChange={(inView) => onSeen(inView, availableQuantityMessage)} triggerOnce={true} threshold={1}>
                        <Typography variant="caption-default" textColor="feedback.urgent" mt="4">
                            {availableQuantityMessage}
                        </Typography>
                    </InView>
                )}
            </Experiment.WhenB>
            {props.isAllOfferSpecSelected && props.errorMessage && (
                <InView onChange={(inView) => onSeen(inView, props.errorMessage)} triggerOnce={true} threshold={1}>
                    <Box mt="4" data-testid="alert-message" data-element-value={props.errorMessage}>
                        <Notification
                            data-testid="booking-options-error-update-notification"
                            data-element-value={props.searchCriteriaStatus}
                            intent="warning"
                            title={props.errorMessage}
                            asset={{ id: 'fill.symbol.circle-info' }}
                            radius="m"
                        />
                    </Box>
                </InView>
            )}
        </>
    );

    return (
        <>
            <Typography variant="body-default-strong" tag="h5">
                {title}
            </Typography>
            <Typography variant="caption-default" mt="4">
                {props.minPassengerMessage}
            </Typography>
            {alertMessage}
        </>
    );
};
BookingRequirementSection.displayName = 'BookingRequirementSection';

const OfferCardPaxPanel: React.FC<PaxPanelProps> = (props) => {
    return (
        <>
            <Feature.Off name={FeatureName.UpdateAvailabilitySlotAlert}>
                <Grid data-testid="booking-options-pax-panel-container">
                    <GridItem sm={12} key="SectionHeader">
                        <BookingRequirementSection {...props} />
                    </GridItem>
                    <GridItem sm={12} key="Section">
                        <PaxCounterSection {...props} />
                    </GridItem>
                </Grid>
            </Feature.Off>
            <Feature.On name={FeatureName.UpdateAvailabilitySlotAlert}>
                <Box data-testid="booking-options-pax-panel-container" display="contents">
                    <div>
                        <BookingRequirementSection {...props} />
                    </div>
                    <div data-testid="booking-options-pax-availability-panel">
                        <PaxCounterSection {...props} />
                    </div>
                </Box>
            </Feature.On>
        </>
    );
};
OfferCardPaxPanel.displayName = 'OfferCardPaxPanel';

export default connect(mapStateToProps, mapDispatchToProps)(withErrorBoundary(OfferCardPaxPanel));
